export interface PanZoomBounds {
	maxPanX: number;
	maxPanY: number;
	canPanX: boolean;
	canPanY: boolean;
}

export interface PanZoomPoint {
	x: number;
	y: number;
}

interface ResolveAnchoredPanZoomOptions {
	anchorX: number;
	anchorY: number;
	fromScale: number;
	toScale: number;
	panX: number;
	panY: number;
}

export function clampPanZoomValue(
	value: number,
	min: number,
	max: number,
	precision = 5,
) {
	const normalized = Math.min(Math.max(value, min), max);
	return Number(normalized.toFixed(precision));
}

export function scalePanZoomValue(
	value: number,
	delta: number,
	factor = 100,
	clamp = 2,
) {
	return (
		value *
		Math.min(1 + Math.abs(delta / factor), clamp) ** Math.sign(delta)
	);
}

export interface ResolvePanZoomBoundsOptions {
	contentWidth: number;
	contentHeight: number;
	viewportWidth: number;
	viewportHeight: number;
	scale: number;
	/**
	 * Extra pan slack (px) beyond scaled overflow — equal on every side.
	 * Iso full-bleed (scale≈1) still needs this to reveal bottom/side
	 * corners with the same outer padding as the top.
	 */
	panPaddingPx?: number;
}

export function resolvePanZoomBounds({
	contentWidth,
	contentHeight,
	viewportWidth,
	viewportHeight,
	scale,
	panPaddingPx = 0,
	/**
	 * `overflow` (default) — CSS scaled-content overflow.
	 * `frustum` — camera lookAt may reach ±half of the zoomed frame (iso).
	 */
	panExtent = "overflow",
}: ResolvePanZoomBoundsOptions & {
	panExtent?: "overflow" | "frustum";
}): PanZoomBounds {
	const pad =
		typeof panPaddingPx === "number" &&
		Number.isFinite(panPaddingPx) &&
		panPaddingPx > 0
			? panPaddingPx
			: 0;
	let maxPanX: number;
	let maxPanY: number;
	if (panExtent === "frustum") {
		/**
		 * Allow lookAt across the full visible-at-zoom-1 half-span when dolly
		 * zoomed: maxPan = (vp × scale) / 2. CSS overflow only gave
		 * vp×(scale−1)/2 and clipped edge follow at high zoom.
		 */
		maxPanX = (Math.max(1, viewportWidth) * Math.max(1e-6, scale)) / 2 + pad;
		maxPanY =
			(Math.max(1, viewportHeight) * Math.max(1e-6, scale)) / 2 + pad;
	} else {
		const scaledWidth = contentWidth * scale;
		const scaledHeight = contentHeight * scale;
		maxPanX = Math.max((scaledWidth - viewportWidth) / 2, 0) + pad;
		maxPanY = Math.max((scaledHeight - viewportHeight) / 2, 0) + pad;
	}

	return {
		maxPanX,
		maxPanY,
		canPanX: maxPanX > 0.5,
		canPanY: maxPanY > 0.5,
	};
}

export function clampPanZoomPoint(
	point: PanZoomPoint,
	bounds: Pick<PanZoomBounds, "maxPanX" | "maxPanY">,
): PanZoomPoint {
	return {
		x: Math.min(Math.max(point.x, -bounds.maxPanX), bounds.maxPanX),
		y: Math.min(Math.max(point.y, -bounds.maxPanY), bounds.maxPanY),
	};
}

export function resolveAnchoredPanZoomPoint({
	anchorX,
	anchorY,
	fromScale,
	toScale,
	panX,
	panY,
}: ResolveAnchoredPanZoomOptions): PanZoomPoint {
	if (fromScale <= 0 || toScale <= 0) {
		return { x: panX, y: panY };
	}

	const ratio = toScale / fromScale;

	return {
		x: anchorX - (anchorX - panX) * ratio,
		y: anchorY - (anchorY - panY) * ratio,
	};
}

interface ContentPointToViewportOptions {
	contentPoint: PanZoomPoint;
	contentWidth: number;
	contentHeight: number;
	viewportWidth: number;
	viewportHeight: number;
	pan: PanZoomPoint;
	scale: number;
}

/** Maps a point in pan-zoom content space to viewport-local coordinates. */
export function contentPointToViewportPoint({
	contentPoint,
	contentWidth,
	contentHeight,
	viewportWidth,
	viewportHeight,
	pan,
	scale,
}: ContentPointToViewportOptions): PanZoomPoint {
	return {
		x:
			viewportWidth / 2 +
			pan.x +
			(contentPoint.x - contentWidth / 2) * scale,
		y:
			viewportHeight / 2 +
			pan.y +
			(contentPoint.y - contentHeight / 2) * scale,
	};
}

export function isPointInsideViewport(
	point: PanZoomPoint,
	viewportWidth: number,
	viewportHeight: number,
	margin = 0,
): boolean {
	return (
		point.x >= margin &&
		point.x <= viewportWidth - margin &&
		point.y >= margin &&
		point.y <= viewportHeight - margin
	);
}

interface IsContentPointCenteredInViewportOptions
	extends ContentPointToViewportOptions {
	tolerancePx?: number;
}

/** True when a content point maps to the viewport center within `tolerancePx`. */
export function isContentPointCenteredInViewport({
	tolerancePx = 32,
	...viewportOptions
}: IsContentPointCenteredInViewportOptions): boolean {
	const point = contentPointToViewportPoint(viewportOptions);
	const centerX = viewportOptions.viewportWidth / 2;
	const centerY = viewportOptions.viewportHeight / 2;
	return (
		Math.abs(point.x - centerX) <= tolerancePx &&
		Math.abs(point.y - centerY) <= tolerancePx
	);
}

interface ResolveViewportEdgePointAlongRayOptions {
	from: PanZoomPoint;
	toward: PanZoomPoint;
	inset: {
		left: number;
		top: number;
		right: number;
		bottom: number;
	};
}

/**
 * First intersection when casting a ray from `from` toward `toward` against an axis-aligned inset rect.
 */
export function resolveViewportEdgePointAlongRay({
	from,
	toward,
	inset,
}: ResolveViewportEdgePointAlongRayOptions): PanZoomPoint | null {
	const dx = toward.x - from.x;
	const dy = toward.y - from.y;
	if (Math.abs(dx) < 1e-6 && Math.abs(dy) < 1e-6) {
		return null;
	}

	const candidates: number[] = [];
	if (dx > 0) {
		const t = (inset.right - from.x) / dx;
		if (t > 0) {
			candidates.push(t);
		}
	} else if (dx < 0) {
		const t = (inset.left - from.x) / dx;
		if (t > 0) {
			candidates.push(t);
		}
	}
	if (dy > 0) {
		const t = (inset.bottom - from.y) / dy;
		if (t > 0) {
			candidates.push(t);
		}
	} else if (dy < 0) {
		const t = (inset.top - from.y) / dy;
		if (t > 0) {
			candidates.push(t);
		}
	}

	if (candidates.length === 0) {
		return null;
	}

	const t = Math.min(...candidates);
	return {
		x: from.x + dx * t,
		y: from.y + dy * t,
	};
}

export interface PanZoomViewportCenterOffset {
	x?: number;
	y?: number;
}

interface ResolvePanToCenterContentPointOptions {
	contentPoint: PanZoomPoint;
	contentWidth: number;
	contentHeight: number;
	scale: number;
	/** Shifts the viewport anchor away from geometric center (viewport px, +y = lower on screen). */
	viewportCenterOffset?: PanZoomViewportCenterOffset;
}

/** Pan offset that places `contentPoint` at the viewport center (before clamping). */
export function resolvePanToCenterContentPoint({
	contentPoint,
	contentWidth,
	contentHeight,
	scale,
	viewportCenterOffset,
}: ResolvePanToCenterContentPointOptions): PanZoomPoint {
	const offsetX = viewportCenterOffset?.x ?? 0;
	const offsetY = viewportCenterOffset?.y ?? 0;
	return {
		x: -(contentPoint.x - contentWidth / 2) * scale + offsetX,
		y: -(contentPoint.y - contentHeight / 2) * scale + offsetY,
	};
}

/** Inverse of {@link resolvePanToCenterContentPoint} — content point under viewport center. */
export function resolveContentPointAtViewportCenter(params: {
	pan: PanZoomPoint;
	contentWidth: number;
	contentHeight: number;
	scale: number;
	viewportCenterOffset?: PanZoomViewportCenterOffset;
}): PanZoomPoint {
	const scale = Math.max(1e-6, params.scale);
	const offsetX = params.viewportCenterOffset?.x ?? 0;
	const offsetY = params.viewportCenterOffset?.y ?? 0;
	return {
		x: params.contentWidth / 2 - (params.pan.x - offsetX) / scale,
		y: params.contentHeight / 2 - (params.pan.y - offsetY) / scale,
	};
}

/** Small upward tweak after geometry-based band centering (viewport px). */
export const SCOPED_PANEL_MAP_BAND_CENTER_OFFSET_NUDGE_PX = -12;

/** Vertical offset from viewport center to the midpoint of the band between top/bottom insets. */
export function resolveScopedPanelMapBandViewportCenterOffsetY({
	topInsetPx,
	bottomInsetPx,
	viewportHeightPx,
	centerOffsetNudgePx = SCOPED_PANEL_MAP_BAND_CENTER_OFFSET_NUDGE_PX,
}: {
	topInsetPx: number;
	bottomInsetPx: number;
	viewportHeightPx: number;
	centerOffsetNudgePx?: number;
}): number {
	if (viewportHeightPx <= 0) {
		return 0;
	}
	const bandCenterY =
		topInsetPx + (viewportHeightPx - topInsetPx - bottomInsetPx) / 2;
	return bandCenterY - viewportHeightPx / 2 + centerOffsetNudgePx;
}
